import React, { ReactElement } from "react";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import { useArgonController } from "context";

interface Percentage {
  color: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark" | "white";
  count: string | number;
  text: string;
}

interface IconProps {
  color: "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark";
  component: ReactElement | string;
}

interface DetailedStatisticsCardProps {
  bgColor?: "transparent" | "white" | "primary" | "secondary" | "info" | "success" | "warning" | "error" | "dark";
  title: string;
  count: string | number;
  percentage?: Percentage;
  icon: IconProps;
  direction?: "right" | "left";
  uniformHeight?: boolean;
}

function DetailedStatisticsCard({
  bgColor = "white",
  title,
  count,
  percentage = {
    color: "success",
    count: 0,
    text: "",
  },
  icon,
  direction = "right",
  uniformHeight = false,
}: DetailedStatisticsCardProps): React.JSX.Element {
  const [controller] = useArgonController();
  const { darkMode } = controller;

  const cardSx = uniformHeight
    ? { height: "100%", width: "100%", minWidth: 0, display: "flex", flexDirection: "column" as const }
    : { width: "100%", maxWidth: "100%" };
  const stretchColumnSx = uniformHeight
    ? { flex: 1, display: "flex", flexDirection: "column" as const, minHeight: 0, width: "100%" }
    : undefined;
  const innerPadSx = uniformHeight
    ? { flex: 1, display: "flex", flexDirection: "column" as const, minHeight: 0, width: "100%" }
    : undefined;
  const gridRowSx = uniformHeight ? { flex: 1, minWidth: 0 } : undefined;
  const descWrapSx = uniformHeight
    ? {
        mt: "auto",
        pt: 1,
        minHeight: "2.5rem",
        display: "flex",
        alignItems: "flex-start",
      }
    : undefined;
  const descTextSx = uniformHeight
    ? {
        fontSize: "0.6875rem",
        lineHeight: 1.25,
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical" as const,
        overflow: "hidden",
        wordBreak: "break-word" as const,
      }
    : undefined;

  const iconBox = (
    <Box
      variant="gradient"
      bgColor={bgColor === "white" ? icon.color : "white"}
      color={bgColor === "white" ? "white" : "dark"}
      width="3rem"
      height="3rem"
      minWidth="3rem"
      borderRadius="section"
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexShrink={0}
      ml={direction === "right" && !uniformHeight ? "auto" : 0}
    >
      {typeof icon.component === "string" ? (
        <Icon fontSize="small" color="inherit">
          {icon.component}
        </Icon>
      ) : (
        <Box
          sx={{
            fontSize: "1.125rem",
            lineHeight: 1,
            display: "grid",
            placeItems: "center",
            color: "inherit",
            "& svg": { fontSize: "1.125rem", color: "inherit" },
          }}
        >
          {icon.component}
        </Box>
      )}
    </Box>
  );

  const titleBlock = (
    <Box flex={1} minWidth={0} ml={direction === "left" ? 2 : 0} lineHeight={1}>
      <Typography
        variant="button"
        color={bgColor === "white" ? "text" : "white"}
        textTransform="uppercase"
        fontWeight="medium"
        sx={{ wordBreak: "break-word" }}
      >
        {title}
      </Typography>
      <Typography
        variant="h5"
        fontWeight="bold"
        color={bgColor === "white" ? "dark" : "white"}
        mb={1}
        sx={{ wordBreak: "break-word" }}
      >
        {count}
      </Typography>
    </Box>
  );

  const headerRow = uniformHeight ? (
    <Box
      display="flex"
      flexDirection="row"
      alignItems="flex-start"
      justifyContent="space-between"
      gap={1.5}
      width="100%"
      minWidth={0}
      sx={gridRowSx}
    >
      {direction === "left" ? iconBox : null}
      {titleBlock}
      {direction === "right" ? iconBox : null}
    </Box>
  ) : (
    <Grid container sx={gridRowSx}>
      {direction === "left" ? <Grid item>{iconBox}</Grid> : null}
      <Grid item xs={8}>
        {titleBlock}
      </Grid>
      {direction === "right" ? (
        <Grid item xs={4}>
          {iconBox}
        </Grid>
      ) : null}
    </Grid>
  );

  return (
    <Card sx={cardSx}>
      <Box
        bgColor={bgColor === "white" && darkMode ? "transparent" : bgColor}
        variant={bgColor === "white" && darkMode ? "contained" : "gradient"}
        sx={stretchColumnSx}
      >
        <Box p={2} sx={innerPadSx}>
          {headerRow}
          <Box sx={descWrapSx}>
            <Typography
              display="flex"
              alignItems="flex-start"
              flexWrap="wrap"
              variant="button"
              fontWeight="bold"
              color={percentage.color}
              sx={uniformHeight ? { lineHeight: 1.2, minWidth: 0 } : { minWidth: 0 }}
            >
              {percentage.count}
              <Typography
                variant={uniformHeight ? "caption" : "body2"}
                component="span"
                fontWeight="regular"
                color={bgColor === "white" ? "text" : "white"}
                ml={0.5}
                mt={uniformHeight ? 0 : -0.125}
                sx={descTextSx}
              >
                {percentage.text}
              </Typography>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Card>
  );
}

export default DetailedStatisticsCard;
